/**
 * A maker's own cards in their own feed, while they wait for a look.
 *
 * An upload or a link goes on the bench (migration 0020): everyone else sees it once a person has
 * opened it on a phone, and until then feed() does not serve it. Its maker should not lose it in
 * the meantime. The card is put next for them when they add it, and it stays in their feed:
 *
 *  - through the end card's re-read (BLITZ there starts over on a fresh feed(), which does not
 *    carry the bench): cards this tab added that the fresh feed lacks are put back next after the
 *    first card, and cards only this tab can run (a dropped .zip or .wasm, `local`) stay at the tail;
 *  - on every visit: the maker's own cards still on the bench (my_cards()), newest first and at
 *    most three, are put next after the card they are on.
 *
 * Once a card is live, feed() serves it in its ranked place and nothing here touches it.
 */
import type { GameMeta } from './protocol';

/** How many of a maker's waiting cards are put next on a visit: the newest few, not the whole bench. */
export const WAITING_NEXT = 3;

/** The maker's own cards to put next: on the bench, not already in the feed, newest first. */
export function waitingToAdd(mine: ReadonlyArray<{ meta: GameMeta; status: string }>, have: readonly GameMeta[], max = WAITING_NEXT): GameMeta[] {
  const ids = new Set(have.map((g) => g.id));
  return mine
    .filter((c) => c.status === 'bench' && !ids.has(c.meta.id))
    .slice(0, max)
    .map((c) => c.meta);
}

/**
 * The end card's re-read. `next` is the fresh feed; `have` is the feed on screen; `addedHere` the
 * ids this tab put in itself (an upload, a link, a card of the maker's opened from their profile,
 * the waiting cards put next on arrival). The same object is kept for a card already on screen:
 * the carousel keys slides by identity, and a new object would rebuild that card's frame.
 */
export function mergeFresh(have: readonly GameMeta[], next: readonly GameMeta[], addedHere: ReadonlySet<string>): GameMeta[] {
  const ids = new Set(next.map((g) => g.id));
  const byId = new Map(have.map((g) => [g.id, g] as const));
  const fresh = next.map((g) => byId.get(g.id) ?? g);
  const waiting = have.filter((g) => !ids.has(g.id) && !g.local && addedHere.has(g.id));
  const local = have.filter((g) => !ids.has(g.id) && g.local);
  return [...fresh.slice(0, 1), ...waiting, ...fresh.slice(1), ...local];
}
